// Catalog items "handed off" from a product card (Entry Point 2's "Add to
// request" on the landing/browse pages, or the older single-item "Request
// price") to whichever screen renders RequestLinesForm next
// (pages/GuestPage.jsx's 'form' step, or pages/RequestFormPage.jsx).
// Deliberately NOT part of either page's own usePersistedState draft — a
// product card is clicked before either flow's component has mounted. One
// flat localStorage key that gets drained on mount is simpler than
// threading a "pending items" prop through both flows' own state machines,
// and it's guest-safe: no account to store it against, just the browser.
//
// Stored as { items: [...], savedAt }, not a bare array — savedAt backs
// the 7-day staleness check below.
//
// LEGACY SHAPE: before this list existed, this key held one bare line
// object (no `items` wrapper) — "Request price" overwrote it outright, and
// whoever mounted next read it as-is. Someone who clicked "Request price"
// before this shipped and hasn't reloaded yet still has that shape sitting
// in their browser; readList() below treats it as a one-item list (treated
// as fresh — there's no way to know its real age, and discarding it would
// just be the exact "my pending item vanished" complaint this is trying to
// avoid) rather than throwing or silently losing it.
const PENDING_KEY = 'sneethel.portal.pendingLine';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;
// Fired on every write so a same-tab listener (the header's pending-count
// badge) can update without polling localStorage — storage events only
// fire for OTHER tabs, never the tab that made the change.
const CHANGED_EVENT = 'sneethel:pendingLineChanged';

function readList() {
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed?.items)) {
      if (Date.now() - (parsed.savedAt || 0) > TTL_MS) return [];
      return parsed.items;
    }
    // Legacy bare-object shape.
    if (parsed && typeof parsed.rawDescription === 'string') return [parsed];
    return [];
  } catch {
    return [];
  }
}

function writeList(items) {
  try {
    if (items.length === 0) window.localStorage.removeItem(PENDING_KEY);
    else window.localStorage.setItem(PENDING_KEY, JSON.stringify({ items, savedAt: Date.now() }));
    window.dispatchEvent(new Event(CHANGED_EVENT));
  } catch {
    // Non-fatal — the destination screen just won't find anything pending.
  }
}

// Adds one line. The browse page's "Add to request" (Entry Point 2) and
// the older "Request price" both call this — "Request price" still
// navigates to /start right after, so it still behaves exactly as before
// (one item, consumed once on arrival); this just makes both paths share
// one mechanism instead of two. Same product added twice bumps qty instead
// of queuing a duplicate row — a buyer tapping "Add to request" again on
// something they already added almost always means "I want more of it."
export function setPendingLine(line) {
  const items = readList();
  const existing = line.matchedProductId
    ? items.find((i) => i.matchedProductId === line.matchedProductId)
    : null;
  if (existing) existing.qty = String(Number(existing.qty || 0) + Number(line.qty || 1));
  else items.push(line);
  writeList(items);
}

// Reads AND clears in one call — drained by mergePendingCatalogItems below
// on every mount of whichever form page gets there, not a one-shot-ever
// flag, so a buyer who adds more while browsing and comes back later still
// picks up the rest.
export function consumePendingLine() {
  const items = readList();
  writeList([]);
  return items;
}

// Non-destructive count, for the header's pending-items badge.
export function peekPendingLineCount() {
  return readList().length;
}

// Drains whatever's pending and folds it into `draft` (a lines-draft shape,
// or null/undefined for "nothing drafted yet") — returns `draft` UNCHANGED
// (same reference) when there's nothing pending, so a caller like
// usePersistedState's `prepare` hook can cheaply tell "did anything change"
// with `!==`.
//
// Meant to run inside that hook's own lazy useState initializer (see
// hooks/usePersistedState.js's own comment on `prepare`) — i.e.
// SYNCHRONOUSLY, during the first render of whichever page holds the lines
// draft, before any child (RequestLinesForm) can capture a stale
// `defaultValues` snapshot. Replaces hooks/useConsumePendingLine.js, which
// did the identical merge but inside a useEffect — correct, but one render
// pass too late for a child whose own form library reads its initial
// values once and never re-syncs from a prop.
export function mergePendingCatalogItems(draft) {
  const pendingItems = consumePendingLine();
  if (pendingItems.length === 0) return draft;
  const base = draft || { neededBy: '', purpose: '', buyerNote: '', lines: [] };
  return { ...base, lines: [...base.lines, ...pendingItems] };
}

export const PENDING_LINE_CHANGED_EVENT = CHANGED_EVENT;
